import {Map, List} from 'immutable';
import {initialDesignId} from '~/utils/initializer';
import {designParamsPresent} from '~/utils/url';
// types
import ApiConstants from '~/constants/ApiConstants';
import ArtTypes from '~/constants/types/ArtTypes';
import CanvasTypes from '~/constants/types/CanvasTypes';
import DesignTypes from '~/constants/types/DesignTypes';
import FundraisingTypes from '~/constants/types/FundraisingTypes';
import IconTypes from '~/constants/types/IconTypes';
import NamesAndNumbersTypes from '~/constants/types/NamesAndNumbersTypes';
import ProductTypes from '~/constants/types/ProductTypes';
import TextTypes from '~/constants/types/TextTypes';
import UploadTypes from '~/constants/types/UploadTypes';
import {makeUndoable} from '~/utils/undoable';

import {isNamesOrNumbers} from '~/data/accessors/icon';

const excludeIds = targetIds => testId => !targetIds.includes(testId);
const isInvalidLayerPosition = position =>
  position === -1 || position === undefined || position === null;

export function initialState() {
  return Map({
    [initialDesignId]: Map({
      apiVersion: ApiConstants.designs.version,
      compositeId: null,
      email: null,
      fetching: designParamsPresent(),
      iconIds: List(),
      id: initialDesignId,
      isCombined: false,
      lastUpdatedAt: new Date().getTime(),
      canUpdate: true,
      name: '',
      inkMatches: Map({})
    })
  });
}

export function design(state = initialState(), action) {
  switch (action.type) {
    case ArtTypes.ADD_ART:
    case NamesAndNumbersTypes.ADD_NAMES:
    case NamesAndNumbersTypes.ADD_NUMBERS:
    case TextTypes.ADD_TEXT:
    case UploadTypes.UPLOAD_COMPLETE:
    case UploadTypes.UPLOAD_AND_SWAP: {
      const {designId, iconId, layerPosition} = action.payload;
      return state.updateIn([designId, 'iconIds'], iconIds =>
        isInvalidLayerPosition(layerPosition)
          ? iconIds.push(iconId)
          : iconIds.insert(layerPosition, iconId)
      );
    }

    case NamesAndNumbersTypes.ADD_NAMES_AND_NUMBERS: {
      const {designId, namesIconId, numbersIconId} = action.payload;
      return state.updateIn([designId, 'iconIds'], iconIds =>
        iconIds.concat(namesIconId, numbersIconId)
      );
    }
    case IconTypes.BACKWARD: {
      const {designId, iconIds} = action.payload;
      return state.updateIn([designId, 'iconIds'], currentIconIds => {
        const iconIdsToStay = currentIconIds.filter(excludeIds(iconIds));
        return List().concat(iconIds, iconIdsToStay);
      });
    }
    case IconTypes.FORWARD: {
      const {designId, iconIds} = action.payload;
      return state.updateIn([designId, 'iconIds'], currentIconIds => {
        const iconIdsToStay = currentIconIds.filter(excludeIds(iconIds));
        return List().concat(iconIdsToStay, iconIds);
      });
    }
    case IconTypes.BACKWARD_STEP: {
      const {designId, iconIds} = action.payload;
      const iconId = iconIds[0];
      return state.updateIn([designId, 'iconIds'], currentIconIds => {
        const prevIndex = currentIconIds.indexOf(iconId);
        if (iconIds.size > 1 || prevIndex === 0) {
          return currentIconIds;
        } else {
          return currentIconIds.delete(prevIndex).insert(prevIndex - 1, iconId);
        }
      });
    }
    case IconTypes.FORWARD_STEP: {
      const {designId, iconIds} = action.payload;
      const iconId = iconIds[0];
      return state.updateIn([designId, 'iconIds'], currentIconIds => {
        const prevIndex = currentIconIds.indexOf(iconId);
        if (iconIds.size > 1 || prevIndex === currentIconIds.size - 1) {
          return currentIconIds;
        } else {
          return currentIconIds.delete(prevIndex).insert(prevIndex + 1, iconId);
        }
      });
    }
    case IconTypes.CUT: {
      const {designId, iconsMap} = action.payload;
      const copyableItems = iconsMap.filter(
        item => !isNamesOrNumbers(item.sourceIconType)
      );

      if (!copyableItems.length) {
        return state;
      }

      return state.updateIn([designId, 'iconIds'], iconIds =>
        iconIds.filter(excludeIds(copyableItems.map(item => item.sourceIconId)))
      );
    }
    case IconTypes.DUPLICATE:
    case IconTypes.PASTE: {
      const {designId, iconsMap} = action.payload;
      return state.updateIn([designId, 'iconIds'], iconIds =>
        iconIds.concat(iconsMap.map(item => item.targetIconId))
      );
    }
    case IconTypes.REMOVE: {
      const {designId, iconIds} = action.payload;
      return state.updateIn([designId, 'iconIds'], currentIconIds =>
        currentIconIds.filter(excludeIds(iconIds))
      );
    }
    case IconTypes.RESTORE: {
      const {designId, targetIconId} = action.payload;
      return state.updateIn([designId, 'iconIds'], iconIds =>
        iconIds.push(targetIconId)
      );
    }
    case NamesAndNumbersTypes.CLEAR: {
      const {designId, iconIds} = action.payload;
      return state.updateIn([designId, 'iconIds'], designIconIds =>
        designIconIds.filter(iconId => !iconIds.includes(iconId))
      );
    }
    case DesignTypes.DUPLICATE_DESIGN: {
      let {design} = action.payload;
      design = design.set('lastUpdatedAt', new Date().getTime());
      return state.set(design.get('id'), design);
    }
    case DesignTypes.SAVE_SUCCESS:
    case FundraisingTypes.SAVE_SUCCESS: {
      const {designId, designName, email, response} = action.payload;
      const selectedDesign = state.get(designId);
      const updatedDesignAttrs = {
        name: designName,
        compositeId: response.design.composite_id,
        email,
        lastUpdatedAt: new Date().getTime()
      };
      if (!selectedDesign) {
        return state.mergeIn([designId], updatedDesignAttrs);
      }
      if (typeof selectedDesign.get('canUpdate') === 'undefined') {
        updatedDesignAttrs['canUpdate'] = true;
      }
      return state.mergeIn([designId], updatedDesignAttrs);
    }
    case DesignTypes.SAVE_FAILED:
    case FundraisingTypes.SAVE_FAILED: {
      const {designId, designName, email, error} = action.payload;
      const errors = (error && error.errors && error.errors.base) || [];
      if (errors.includes('design_exists')) {
        return state.mergeIn([designId], {
          compositeId: null,
          name: designName,
          email
        });
      }
      return state;
    }
    case DesignTypes.CLEAR_DESIGN_DATA:
    case CanvasTypes.CLEAR:
      return initialState().setIn([initialDesignId, 'fetching'], false);
    case DesignTypes.FETCH_SUCCESS: {
      let {design} = action.payload;
      if (typeof window.FS !== 'undefined') {
        window.FS.identify(design.get('email'));
      }
      design = design.set('lastUpdatedAt', new Date().getTime());
      return state.set(design.get('id'), design);
    }
    case DesignTypes.FETCH_FAILED:
      return state.setIn([action.payload.designId, 'fetching'], false);
    case DesignTypes.FETCH:
      return state.setIn([action.payload.designId, 'fetching'], true);
    case DesignTypes.SET_COMBINE_DESIGNS:
      return state.setIn(
        [action.payload.designId, 'isCombined'],
        action.payload.isCombined
      );
    case ProductTypes.SWAP_PRODUCT: {
      const {designId, newViewIcons, canvasViewIcons} = action.payload;
      const newIconIds = [];
      for (const view of Object.keys(newViewIcons)) {
        if (canvasViewIcons.get(parseInt(view, 10))) {
          const iconsForView = canvasViewIcons.get(parseInt(view, 10));
          for (const icon of iconsForView) {
            newIconIds.push(icon.id);
          }
        }
      }
      return state.setIn([designId, 'iconIds'], List(newIconIds));
    }
    case DesignTypes.UPDATE_INK_MATCHES: {
      const {designId, inkMatches} = action.payload;
      return state.setIn([designId, 'inkMatches'], Map(inkMatches));
    }
    case DesignTypes.REMOVE_INK_MATCH: {
      const {designId, hexString} = action.payload;
      return state.deleteIn([designId, 'inkMatches', hexString]);
    }
    case DesignTypes.RESET_INK_MATCHES: {
      const {designId} = action.payload;
      return state.setIn([designId, 'inkMatches'], Map({}));
    }
    default:
      return state;
  }
}

export default makeUndoable(design);
